import { sdkLog } from '../decorator';
import JsSdkBase from '../core/js-sdk-base';
import { asyncLoadScript, isEmpty, isNil, map } from '@libs/shared';
import { autoUnimplemented } from '../decorator/app';
import {
    APP_ERROR_CODE,
    SdkError,
    SDKFunction,
    SdkType,
    type SetHeaderActionsParams,
    type ClearHeaderActionsParams,
    type HeaderActionClickEvent,
    type AddToDesktopParams,
    type AddToDesktopResult
} from '../interface';
import { IMAGE_SUPPORT_TYPE, JSSDK_URL, SdkResponseCode } from '../constant';

/**
 * Jodoo App环境下的SDK
 * @see https://fanruan-x.feishu.cn/wiki/UiuqwcfhViEdfIk0KgAcxdCdnjd?fromScene=spaceOverview
 */
export default class JodooSdk extends JsSdkBase {
    constructor() {
        super();
        this.sdkType = SdkType.Jodoo;
    }

    @sdkLog()
    loadScript() {
        return asyncLoadScript(JSSDK_URL.App.Jodoo);
    }

    @sdkLog()
    init() {
        /**
         * app环境下不检查jssdk config
         * @see g-6233589787 73ed108d
         * @see https://code.fineres.com/projects/FX/repos/fx-code-mobile/pull-requests/6623/diff#src/models/jssdk/jsSdk.ts
         */
        return this.asyncLoadScript().then(() => {
            this.setIsSdkReady(true);
        });
    }

    @autoUnimplemented
    @sdkLog()
    setPageTitle(title: string) {
        return window.fx.updateTitle({ title });
    }

    @autoUnimplemented
    @sdkLog()
    scanCode(options: Parameters<JsSdkBase['scanCode']>[0]) {
        let scanType: ('qrCode' | 'barCode')[] = ['qrCode', 'barCode'];
        switch (options.type) {
            case 'qrCode':
                scanType = ['qrCode'];
                break;
            case 'barCode':
                scanType = ['barCode'];
                break;
        }
        return window.fx.scanCode({ type: scanType });
    }

    @autoUnimplemented
    @sdkLog()
    switchCorp(tenantId?: string) {
        return window.fx.switchCorp({ tenantId });
    }

    @autoUnimplemented
    @sdkLog()
    signout() {
        return window.fx.signout();
    }

    @autoUnimplemented
    @sdkLog()
    requestCameraPermission() {
        return window.fx.requestCameraPermission();
    }

    @autoUnimplemented
    @sdkLog()
    previewImage(urls: string[], current: string) {
        const currentIndex = urls.indexOf(current);
        return window.fx.previewImage({
            urls: urls,
            current: currentIndex > -1 ? currentIndex : 0
        });
    }

    @sdkLog()
    getRequestHeaders() {
        const systemInfo = window.fx?.getSystemInfoSync();
        if (isEmpty(systemInfo)) {
            return {} as any as Record<string, string>;
        }
        return {
            Authentication: systemInfo.authentication || '',
            'x-app-ver': systemInfo.version || '',
            'x-device-id': systemInfo.deviceId || ''
        };
    }

    @sdkLog()
    deletedAccountToken() {
        const systemInfo = window.fx?.getSystemInfoSync?.();
        return systemInfo?.deletedAccountToken;
    }

    @sdkLog()
    onCameraPermissionChanged(callback: (res: string) => void) {
        return window.fx.onCameraPermissionChanged(callback);
    }

    @autoUnimplemented
    @sdkLog()
    chooseImage(options: Parameters<JsSdkBase['chooseImage']>[0]) {
        return new Promise<File[]>((resolve, reject) => {
            window.fx
                .chooseImage({
                    count: options.maxFileCount || 1,
                    sizeType: options.compressed ? 'compressed' : 'original',
                    sourceType: options.onlyCamera ? ['camera'] : ['album', 'camera'],
                    cameraDevice: 'back',
                    includeBase64: false,
                    saveToAlbum: false
                })
                .then((res: any) => {
                    const files: any[] = [];
                    const readFiles = map(res.files, ({ path, size, type, fileName }) => {
                        // 图片类型映射，将 jpg 统一转换为 jpeg 以避免水印生成时 canvas.toBlob 按 png 处理导致体积增大10x
                        let mime = 'image/jpeg'; // 默认值
                        if (type === 'image/jpg') {
                            mime = 'image/jpeg';
                        } else if (IMAGE_SUPPORT_TYPE.includes(type)) {
                            mime = type;
                        }
                        return new Promise((resolve, reject) => {
                            const slice = Math.floor((1024 * 1024) / 3) * 3; // 分片1M base64需要3的倍数

                            this.readFileBySlice(path, size, slice)
                                .then((chunks) => {
                                    const base64Str = chunks.join('');
                                    // 将 base64 字符串转换为二进制数据
                                    const binaryString = atob(base64Str);
                                    const u8arr = new Uint8Array(binaryString.length);
                                    for (let i = 0; i < binaryString.length; i++) {
                                        u8arr[i] = binaryString.charCodeAt(i);
                                    }
                                    // 创建 Blob 对象
                                    const blob = new Blob([u8arr], { type: mime });

                                    // 创建 File 对象
                                    const file = new File([blob], fileName, { type: mime });

                                    files.push(file);
                                    resolve(null);
                                })
                                .catch((err) => {
                                    reject(
                                        new SdkError(
                                            this.sdkType,
                                            SdkResponseCode.ThirdPartyServiceFail,
                                            JSON.stringify(err)
                                        )
                                    );
                                });
                        });
                    });
                    Promise.all(readFiles).then(() => {
                        resolve(files);
                    }, reject);
                })
                .catch((err: any) => {
                    // 主动取消
                    if (err.code === '200001') {
                        resolve([]);
                    } else {
                        reject(err);
                    }
                });
        });
    }

    readFileBySlice(path: string, total: number, slice: number) {
        // 分片文件总缓存
        // 返回base64字符串
        const chunks: string[] = [];

        // 读取单个分片
        const readSingleSlice = (currentPosition: number): Promise<void> => {
            return new Promise((resolve, reject) => {
                window.fx
                    .readFile({
                        path: path,
                        position: currentPosition,
                        length: slice < total ? slice : total
                    })
                    .then((res: { errMsg: string; data: string }) => {
                        const { data } = res;
                        try {
                            chunks.push(data);
                            resolve();
                        } catch (error) {
                            reject(error);
                        }
                    })
                    .catch((err: any) => {
                        reject(err);
                    });
            });
        };

        // 使用异步函数直接处理所有分片
        const readAllSlices = async (): Promise<string[]> => {
            // 读取图片的初始位置为 0
            let currentPosition = 0;

            // 循环处理直到完成
            while (currentPosition < total) {
                // 等待当前分片处理完成
                await readSingleSlice(currentPosition);
                currentPosition = currentPosition + slice;
            }

            // 所有分片处理完成，返回结果
            return chunks;
        };
        return readAllSlices();
    }

    @sdkLog()
    downloadPrintFile({
        url,
        fileType,
        fileName
    }: {
        url: string;
        fileType?: string;
        fileName?: string;
    }) {
        return this.downloadLink({ url, fileType, fileName, share: true });
    }

    @sdkLog()
    downloadLink({
        url,
        fileType,
        fileName,
        share,
        onProgress
    }: {
        url: string;
        fileType?: string;
        fileName?: string;
        share?: boolean;
        onProgress?: (options: { total: number; current: number }) => void;
    }) {
        return window.fx
            .downloadFile({ url, type: fileType, name: fileName, share, onProgress })
            .catch((err: any) => {
                if (err?.code === APP_ERROR_CODE.METHOD_NOT_FOUND) {
                    // 未实现
                    return super.downloadLink({ url });
                }
                return Promise.reject(err);
            });
    }

    @sdkLog()
    cancelDownloadLink({ url }: { url: string }) {
        return window.fx.cancelDownloadFile({ url });
    }

    /** 设置当前网页的导航栏业务按钮。 */
    @sdkLog()
    async setHeaderActions(params: SetHeaderActionsParams): Promise<void> {
        if (typeof window.fx?.navigation?.setHeaderActions !== 'function') {
            return super.setHeaderActions(params);
        }
        try {
            return await window.fx.navigation.setHeaderActions(params);
        } catch (error) {
            if ((error as { code?: string })?.code === APP_ERROR_CODE.METHOD_NOT_FOUND) {
                return super.setHeaderActions(params);
            }
            throw error;
        }
    }

    /** 清除当前网页配置，兼容缺少原生方法的旧 App。 */
    @sdkLog()
    async clearHeaderActions(params?: ClearHeaderActionsParams): Promise<void> {
        if (typeof window.fx?.navigation?.clearHeaderActions !== 'function') {
            return super.clearHeaderActions(params);
        }
        try {
            return await window.fx.navigation.clearHeaderActions(params);
        } catch (error) {
            if ((error as { code?: string })?.code === APP_ERROR_CODE.METHOD_NOT_FOUND) {
                return super.clearHeaderActions(params);
            }
            throw error;
        }
    }

    /** 直接透传同步取消函数。 */
    @sdkLog()
    onHeaderActionClick(callback: (event: HeaderActionClickEvent) => void): () => void {
        if (typeof window.fx?.navigation?.onHeaderActionClick !== 'function') {
            return super.onHeaderActionClick(callback);
        }
        return window.fx.navigation.onHeaderActionClick(callback);
    }

    /** 透传添加结果和业务错误。 */
    @sdkLog()
    async addToDesktop(params: AddToDesktopParams): Promise<AddToDesktopResult> {
        if (typeof window.fx?.device?.shortcut?.addToDesktop !== 'function') {
            return super.addToDesktop(params);
        }
        try {
            return await window.fx.device.shortcut.addToDesktop(params);
        } catch (error) {
            if ((error as { code?: string })?.code === APP_ERROR_CODE.METHOD_NOT_FOUND) {
                return super.addToDesktop(params);
            }
            throw error;
        }
    }

    @sdkLog()
    hasFunction(functionName: SDKFunction): Promise<boolean> {
        const functionMap = {
            [SDKFunction.ChooseImage]: 'chooseImage',
            [SDKFunction.DownloadLink]: 'downloadFile',
            [SDKFunction.GoToAboutApp]: 'goToAboutApp',
            [SDKFunction.GoToCreateCorp]: 'goToCreateCorp',
            [SDKFunction.SetHeaderActions]: 'setHeaderActions',
            [SDKFunction.ClearHeaderActions]: 'clearHeaderActions',
            [SDKFunction.AddToDesktop]: 'addToDesktop'
        };
        const name = functionMap[functionName];
        if (!name) {
            return Promise.resolve(false);
        }
        return window.fx
            .hasFunction({ name })
            .then((res: any) => {
                return !!res.result;
            })
            .catch(() => {
                return false;
            });
    }

    @autoUnimplemented
    @sdkLog()
    goToAboutApp(): Promise<void> {
        return window.fx.goToAboutApp();
    }

    @autoUnimplemented
    @sdkLog()
    goToCreateCorp(): Promise<void> {
        return window.fx.goToCreateCorp();
    }

    @autoUnimplemented
    @sdkLog()
    getLocation(
        options: Parameters<JsSdkBase['getLocation']>[0]
    ): ReturnType<JsSdkBase['getLocation']> {
        if (typeof window.fx?.getLocation !== 'function') {
            return super.getLocation(options);
        }
        return new Promise((resolve, reject) => {
            window.fx
                .getLocation()
                .then((res: any) => {
                    const lng = res.longitude;
                    const lat = res.latitude;
                    if (!isNil(lng) && !isNil(lat)) {
                        const position = [lng, lat];
                        resolve([position, res]);
                    } else {
                        reject(
                            new SdkError(
                                this.sdkType,
                                SdkResponseCode.Location.LngOrLatEmpty,
                                `longitude or latitude is empty, longitude: ${lng}, latitude: ${lat}`
                            )
                        );
                    }
                })
                .catch((err: any) => {
                    if (err?.code === APP_ERROR_CODE.METHOD_NOT_FOUND) {
                        super
                            .getLocation(options)
                            .then(resolve)
                            .catch((err) => {
                                reject(
                                    new SdkError(
                                        SdkType.Browser,
                                        SdkResponseCode.ThirdPartyServiceFail,
                                        JSON.stringify(err)
                                    )
                                );
                            });
                        return;
                    }
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                });
        });
    }

    /**
     * 重置旋转屏幕
     */

    @autoUnimplemented
    @sdkLog()
    resetScreenRotate() {
        return window.fx.resetScreenView();
    }

    @autoUnimplemented
    @sdkLog()
    screenRotateBeforeFullscreen(fullscreen: boolean): Promise<null> {
        if (fullscreen) {
            return window.fx.rotateScreenView();
        } else {
            return window.fx.resetScreenView();
        }
    }
}
